import { Link, PrimaryButton } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox/checkbox';
import { Dialog, DialogTitle, DialogWindow } from '../../../../widgets/dialog';
import { NotificationBanner } from '../../../../widgets/notificationBanner/notificationBanner';
import React, { FC, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { stringFormat, T } from 'src/shared/helpers';
import config from 'src/shared/config';
import styled from 'styled-components';
import printfulStoreSelectImage from 'assets/images/others/printful-store-select.png';
import printfulAuthUrlQueryNode, { printfulAuthUrlQuery } from './__generated__/printfulAuthUrlQuery.graphql';
import { printfulIntegrationWizardEcommerceQuery } from './__generated__/printfulIntegrationWizardEcommerceQuery.graphql';
import { GRAPHQL_URL } from 'src/relay/fetchQuery';
import { ECommerces, ProductProviders } from '../providerElements';
import { clearWizardProgress, loadWizardProgress, saveWizardProgress } from '../wizardProgress';

const PRINTFUL_SIGNUP_URL = 'https://www.printful.com/auth/register?utm_source=portal.zakeke.com&utm_medium=link&utm_content=affId-7507&utm_campaign=affiliate';

const PLATFORM_INFO: Record<number, { name: string; guideUrl: string }> = {
    [ECommerces.Shopify]: {
        name: 'Shopify',
        guideUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360010218359-How-to-connect-and-use-Printful-with-Zakeke-on-Shopify'
    },
    [ECommerces.WooCommerce]: {
        name: 'WooCommerce',
        guideUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360012599899-How-to-connect-and-use-Printful-with-Zakeke-on-WooCommerce'
    },
    [ECommerces.BigCommerce]: {
        name: 'BigCommerce',
        guideUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360019381180-How-to-connect-and-use-Printful-with-Zakeke-on-BigCommerce'
    }
};

const DEFAULT_GUIDE_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/360010218359-How-to-connect-and-use-Printful-with-Zakeke-on-Shopify';

const StyledDialogWindow = styled(DialogWindow)`
    max-width: 700px;
    height: calc(90vh - 20px);
    padding-left: 24px !important;
    padding-right: 24px !important;
    display: flex;
    flex-direction: column;

    > div {
        overflow: visible;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }
`;

const DialogCaption = styled.p`
    font-size: 13px;
    line-height: 18px;
    color: #6b6b6b;
    margin: -16px 0 20px;
`;

const WizardContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const StepIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 32px;
`;

const StepDot = styled.div<{ $active: boolean; $completed: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => (props.$active || props.$completed ? '#fff' : '#9e9e9e')};
    background: ${(props) => (props.$completed ? '#2e7d32' : props.$active ? props.theme.app.primary : '#e0e0e0')};
    transition: all 0.2s ease;
`;

const StepLine = styled.div<{ $completed: boolean }>`
    width: 40px;
    height: 2px;
    background: ${(props) => (props.$completed ? '#2e7d32' : '#e0e0e0')};
    transition: all 0.2s ease;
`;

const StepContentArea = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
`;

const StepTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
`;

const StepBody = styled.div`
    font-size: 13px;
    line-height: 18px;
    color: #333;
    display: flex;
    flex-direction: column;
    gap: 8px;

    p {
        margin: 4px 0;
    }

    ul {
        margin: 4px 0;
    }

    ${NotificationBanner} {
        margin: 4px 0;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 8px;
    padding-top: 8px;
`;

const StoreSelectionImage = styled.img`
    width: 50%;
    border-radius: 6px;
    border: 1px solid #dbe2e6;
    align-self: center;
    flex: 1;
    min-height: 0;
    object-fit: contain;
`;

const IntegrateContainer = styled.div`
    padding: 32px;
    display: flex;
    justify-content: center;
`;

const GuideLinkSpacer = styled.div`
    flex: 1;
    min-height: 20px;
`;

const GuideLink = styled.div`
    padding: 12px 0 0;
    border-top: 1px solid #dbe2e6;
`;

const SuccessMessage = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #2e7d32;
    text-align: center;
    padding: 12px;
`;

// --- Step Indicator Component ---

const StepIndicatorBar: FC<{ totalSteps: number; currentStep: number; completedSteps: boolean[] }> = ({ totalSteps, currentStep, completedSteps }) => {
    return (
        <StepIndicator>
            {Array.from({ length: totalSteps }, (_, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <StepLine $completed={completedSteps[i - 1]} />}
                    <StepDot $active={currentStep === i} $completed={completedSteps[i]}>
                        {completedSteps[i] ? '\u2713' : i + 1}
                    </StepDot>
                </React.Fragment>
            ))}
        </StepIndicator>
    );
};

interface PrintfulIntegrationWizardProps {
    onAuthorized: () => void;
    onConnected?: () => void;
}

const PrintfulIntegrationWizard: FC<PrintfulIntegrationWizardProps> = ({ onAuthorized, onConnected }) => {
    const data = useLazyLoadQuery<printfulAuthUrlQuery>(printfulAuthUrlQueryNode, {});
    const ecommerceData = useLazyLoadQuery<printfulIntegrationWizardEcommerceQuery>(
        graphql`
            query printfulIntegrationWizardEcommerceQuery {
                ecommerceSettings {
                    ecommerceID
                }
            }
        `,
        {}
    );
    const ecommerceID = ecommerceData.ecommerceSettings?.ecommerceID ?? null;
    const platformInfo = ecommerceID != null ? PLATFORM_INFO[ecommerceID] : undefined;
    const platformName = platformInfo?.name;
    const guideUrl = platformInfo?.guideUrl ?? DEFAULT_GUIDE_URL;

    const savedProgress = useRef(loadWizardProgress(ProductProviders.Printful));
    const [currentStep, setCurrentStep] = useState(savedProgress.current?.step ?? 0);
    const [step0Confirmed, setStep0Confirmed] = useState(savedProgress.current?.confirmations[0] ?? false);
    const [step1Done, setStep1Done] = useState(savedProgress.current?.confirmations[1] ?? false);
    const [step2Confirmed, setStep2Confirmed] = useState(savedProgress.current?.confirmations[2] ?? false);
    const [step3Confirmed, setStep3Confirmed] = useState(savedProgress.current?.confirmations[3] ?? false);

    useEffect(() => {
        if (currentStep >= 4) return;
        if (!step1Done) return;
        saveWizardProgress(ProductProviders.Printful, currentStep, [step0Confirmed, step1Done, step2Confirmed, step3Confirmed]);
    }, [currentStep, step0Confirmed, step1Done, step2Confirmed, step3Confirmed]);

    const authWindowRef = useRef<Window | null>(null);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'printful-auth-success') {
                setStep1Done(true);
                setCurrentStep(2);
                onConnected?.();
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onConnected]);

    const checkPrintfulIntegration = async (): Promise<boolean> => {
        try {
            const res = await fetch(GRAPHQL_URL, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + config.token,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'Accept-Language': config.culture
                },
                body: JSON.stringify({ query: '{ apiKey { providerId } }' })
            });
            const json = await res.json();
            return json.data?.apiKey?.some((k: { providerId: number }) => k.providerId === 1) ?? false;
        } catch {
            return false;
        }
    };

    const handleAuthorize = () => {
        const oauthUrl = data.printfulAuthUrl!;
        const left = screen.availWidth / 2 - 400;
        const top = screen.availHeight / 2 - 350;
        const win = window.open(oauthUrl, '_blank', `toolbar=0,location=0,menubar=0,width=715,height=715,left=${left},top=${top}`);
        authWindowRef.current = win;

        // Fallback: when popup closes, verify auth status with the backend
        const checker = setInterval(async () => {
            if (win && win.closed) {
                clearInterval(checker);
                setStep1Done((alreadyDone) => {
                    if (!alreadyDone) {
                        // postMessage didn't fire, check backend
                        checkPrintfulIntegration().then((integrated) => {
                            if (integrated) {
                                setStep1Done(true);
                                setCurrentStep(2);
                                onConnected?.();
                            }
                        });
                    }
                    return alreadyDone;
                });
            }
        }, 500);
    };

    const handleStep0Toggle = (checked: boolean) => {
        if (checked) {
            setStep0Confirmed(true);
            setCurrentStep(1);
        } else {
            setStep0Confirmed(false);
            setStep1Done(false);
            setStep2Confirmed(false);
            setStep3Confirmed(false);
            setCurrentStep(0);
        }
    };

    const handleStep2Toggle = (checked: boolean) => {
        if (checked) {
            setStep2Confirmed(true);
            setCurrentStep(3);
        } else {
            setStep2Confirmed(false);
            setStep3Confirmed(false);
            setCurrentStep(2);
        }
    };

    const handleStep3Toggle = (checked: boolean) => {
        if (checked) {
            setStep3Confirmed(true);
            setCurrentStep(4);
            clearWizardProgress(ProductProviders.Printful);
            onAuthorized();
        } else {
            setStep3Confirmed(false);
            setCurrentStep(3);
        }
    };

    const completedSteps = [step0Confirmed, step1Done, step2Confirmed, step3Confirmed];

    return (
        <WizardContainer>
            <StepIndicatorBar totalSteps={4} currentStep={currentStep} completedSteps={completedSteps} />

            <StepContentArea>
                {currentStep === 0 && (
                    <StepBody>
                        <StepTitle>{T._('Create a Printful account and connect it to your store', 'Admin')}</StepTitle>
                        <p>{T._('To make use of the Zakeke and Printful integration, you will need to:', 'Admin')}</p>
                        <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>
                                <Link target='_blank' href={PRINTFUL_SIGNUP_URL}>
                                    {T._('Create a Printful account', 'Admin')}
                                </Link>
                            </li>
                            <li>
                                {platformName
                                    ? stringFormat(
                                          T._('Connect it to your {0} store from Stores > Choose Platform > {0} in the Zakeke backoffice.', 'Admin'),
                                          platformName
                                      )
                                    : T._('Connect it to your store from Stores > Choose Platform in the Zakeke backoffice.', 'Admin')}
                            </li>
                        </ul>
                        <NotificationBanner
                            type={'WARNING'}
                            variant={'slim'}
                            content={T._('Complete both actions before continuing. The next steps require a connected Printful account.', 'Admin')}
                        />
                        <Checkbox
                            checked={step0Confirmed}
                            onChange={handleStep0Toggle}
                            label={T._('I have created my Printful account and connected it to my store', 'Admin')}
                        />
                    </StepBody>
                )}

                {currentStep === 1 && (
                    <StepBody>
                        <StepTitle>{T._('Connect your Printful account', 'Admin')}</StepTitle>
                        <NotificationBanner
                            type={'WARNING'}
                            variant={'slim'}
                            content={T._(
                                'When authorizing, make sure to select the store that is configured as Master Integration in Zakeke. The Print on Demand integration only works with the master store, not with sales channels.',
                                'Admin'
                            )}
                        />
                        <StoreSelectionImage src={printfulStoreSelectImage} alt={T._('Select the correct store in Printful', 'Admin')} />
                    </StepBody>
                )}

                {currentStep === 2 && (
                    <StepBody>
                        <StepTitle>{T._('Configure sync settings on Printful', 'Admin')}</StepTitle>
                        <p>{T._('Go to your Printful dashboard and configure the sync settings:', 'Admin')}</p>
                        <p>
                            <Link target='_blank' href={'https://www.printful.com/dashboard/settings/store-orders'}>
                                <strong>Settings</strong> {'>'} <strong>Orders</strong> {'>'} <strong>Sync as you go</strong>
                            </Link>
                        </p>
                        <p>{T._('Make sure the following option is checked:', 'Admin')}</p>
                        <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>
                                <strong>{T._('Import existing products', 'Admin')}</strong>
                            </li>
                        </ul>
                        <NotificationBanner
                            type={'WARNING'}
                            variant={'slim'}
                            content={T._('If this option is not enabled, the integration will not work correctly.', 'Admin')}
                        />
                        <Checkbox checked={step2Confirmed} onChange={handleStep2Toggle} label={T._('I have enabled this option in Printful', 'Admin')} />
                    </StepBody>
                )}

                {currentStep === 3 && (
                    <StepBody>
                        <StepTitle>{T._('Verify billing currency on Printful', 'Admin')}</StepTitle>
                        <p>{T._('Go to your Printful dashboard:', 'Admin')}</p>
                        <p>
                            <Link target='_blank' href={'https://www.printful.com/dashboard/billing/billing-methods'}>
                                <strong>Billing</strong> {'>'} <strong>Billing Methods</strong>
                            </Link>
                        </p>
                        <NotificationBanner
                            type={'WARNING'}
                            variant={'slim'}
                            content={T._(
                                'Make sure that the currency set in Printful is the same as your store currency. A currency mismatch will cause issues with order processing.',
                                'Admin'
                            )}
                        />
                        <Checkbox
                            checked={step3Confirmed}
                            onChange={handleStep3Toggle}
                            label={T._('I have verified that the billing currency matches my store', 'Admin')}
                        />
                    </StepBody>
                )}

                {currentStep === 4 && <SuccessMessage>{T._('Printful integration is complete! You are all set.', 'Admin')}</SuccessMessage>}
            </StepContentArea>

            <GuideLinkSpacer />

            {currentStep === 1 && !step1Done && (
                <ButtonContainer style={{ paddingBottom: 12 }}>
                    <PrimaryButton size='small' onClick={handleAuthorize}>
                        {T._('Authorize', 'Admin')}
                    </PrimaryButton>
                </ButtonContainer>
            )}

            <GuideLink>
                <Link target='_blank' href={guideUrl}>
                    {T._('Need help? Read the full integration guide', 'Admin')}
                </Link>
            </GuideLink>
        </WizardContainer>
    );
};

export const PrintfulIntegrationDialog: FC<{ onClose: () => void; onIntegrated: () => void; onConnected?: () => void }> = ({
    onClose,
    onIntegrated,
    onConnected
}) => {
    return (
        <Dialog windowDecorator={StyledDialogWindow} onClose={onClose}>
            <DialogTitle>
                {T._('Connect', 'Admin')} Printful {T._('to your Zakeke account', 'Admin')}
            </DialogTitle>
            <DialogCaption>{T._('Sell Printful products as customizable on your store with automatic fulfillment and drop-shipping.', 'Admin')}</DialogCaption>
            <PrintfulIntegrationWizard onAuthorized={onIntegrated} onConnected={onConnected} />
        </Dialog>
    );
};

export const PrintfulIntegrateButton: FC<{ onIntegrated: () => void }> = ({ onIntegrated }) => {
    const [showDialog, setShowDialog] = useState(false);

    return (
        <>
            <IntegrateContainer>
                <PrimaryButton size='small' onClick={() => setShowDialog(true)}>
                    {T._('Integrate', 'Admin')}
                </PrimaryButton>
            </IntegrateContainer>
            {showDialog && <PrintfulIntegrationDialog onClose={() => setShowDialog(false)} onIntegrated={onIntegrated} />}
        </>
    );
};

export default PrintfulIntegrationWizard;
